import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import MeetingLiveChart from "@common/components/LiveCharts/MeetingLiveChart";
import {
  inThePast,
  isParticipantConnected,
  isParticipantManaged,
} from "@common/utils/meetings";
import { Box, Stack, styled } from "@mui/material";
import { ConferenceParticipant } from "@services/meetings/types";
import { GraphType } from "@services/monitoring/types";
import { CardHolder } from "..";
import { CardContent } from "../OverviewCard";
import { getColorGraph, getGraphTitle } from "../const";

const CardContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  width: "100%",
  marginBottom: theme.spacing(1.25),
  "& > .MuiBox-root": {
    width: "100%",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gridColumn: "span 1",
  gap: theme.spacing(2),
  margin: theme.spacing(2, 1.5),
}));

const DeviceCard = ({
  participant,
  hideHeader = false,
  hideFooter = false,
}: {
  hideHeader?: boolean;
  hideFooter?: boolean;
  participant: ConferenceParticipant;
}) => {
  const { t } = useTranslation();
  const context = useContext(RoomsRealtimeContext);
  const meeting = { ...context.detail?.meeting };
  const startDate = meeting.start;
  const endDate =
    (meeting.end && meeting.end > new Date().getTime()) ||
    meeting.end === undefined
      ? new Date().getTime()
      : meeting.end;

  const breadcrumbPage = meeting.meetingId?.toString() ?? "Meeting";

  const displayGraphContent = () => {
    if (
      isParticipantManaged(participant) &&
      (isParticipantConnected(participant) || inThePast(meeting.end))
    ) {
      return (
        <>
          <MeetingLiveChart
            start={startDate}
            end={endDate}
            title={getGraphTitle(GraphType.Ping_Latency, t)}
            graphType={GraphType.Ping_Latency}
            color={getColorGraph(GraphType.Ping_Latency)}
            device={participant.device}
            breadcrumbPage={breadcrumbPage}
          />

          <MeetingLiveChart
            start={startDate}
            end={endDate}
            title={getGraphTitle(GraphType.Frame_Rate, t)}
            graphType={GraphType.Frame_Rate}
            color={getColorGraph(GraphType.Frame_Rate)}
            device={participant.device}
            breadcrumbPage={breadcrumbPage}
          />

          <MeetingLiveChart
            start={startDate}
            end={endDate}
            title={getGraphTitle(GraphType.Call_Rate, t)}
            graphType={GraphType.Call_Rate}
            color={getColorGraph(GraphType.Call_Rate)}
            device={participant.device}
            breadcrumbPage={breadcrumbPage}
          />

          <MeetingLiveChart
            start={startDate}
            end={endDate}
            title={getGraphTitle(GraphType.Packet_Loss, t)}
            graphType={GraphType.Packet_Loss}
            color={getColorGraph(GraphType.Packet_Loss)}
            device={participant.device}
            breadcrumbPage={breadcrumbPage}
          />

          <MeetingLiveChart
            start={startDate}
            end={endDate}
            title={getGraphTitle(GraphType.Jitter_Audio, t)}
            graphType={GraphType.Jitter_Audio}
            color={getColorGraph(GraphType.Jitter_Audio)}
            device={participant.device}
            breadcrumbPage={breadcrumbPage}
          />
        </>
      );
    }

    return null;
  };

  return (
    <CardContainer>
      <CardHolder device={participant.device}>
        <ContentContainer>
          <CardContent
            device={participant.device}
            participant={participant}
            hideHeader={hideHeader}
            hideFooter={hideFooter}
          />
        </ContentContainer>

        <Stack
          sx={{
            mx: 1.5,
            my: 2,
            gap: 2,
          }}
        >
          {displayGraphContent()}
        </Stack>
      </CardHolder>
    </CardContainer>
  );
};

export default DeviceCard;
